import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShare2, FiMessageCircle, FiMail, FiCopy, FiCheck, FiX, FiDownload, FiLoader } = FiIcons;

const MenuShareButton = ({ menu, isNextWeek = false }) => {
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGeneratingEml, setIsGeneratingEml] = useState(false);

  // 🔥 SICHERE EMOJI-KODIERUNG mit Unicode-Escape-Sequenzen
  const createWhatsAppText = () => {
    const weekText = isNextWeek ? ' (nächste Woche)' : '';
    const dateText = menu.formattedDate ? ` - ${menu.formattedDate}` : '';
    
    // Verwende Unicode-Escape-Sequenzen für sichere Emoji-Darstellung
    const dish = '\uD83C\uDF7D\uFE0F'; // 🍽️
    const calendar = '\uD83D\uDCC5'; // 📅
    const food = '\uD83C\uDF72'; // 🍲
    const money = '\uD83D\uDCB0'; // 💰
    const clock = '\u23F0'; // ⏰
    const location = '\uD83D\uDCCD'; // 📍
    const house = '\uD83C\uDFE0'; // 🏠
    const globe = '\uD83C\uDF10'; // 🌐

    return `${dish} *Mittagsmenü${weekText}*

${calendar} *${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}${dateText}*

${food} *${menu.name}*
${menu.description}

${money} *CHF ${menu.price.toFixed(2)}*
${clock} Servierzeit: 12:00 Uhr
${location} Bahnhofstrasse 19, Au St. Gallen

---
${house} *Hopp Mittagsmenü Service*
Täglich frische, regionale Küche
${globe} wochenmenu.hopp.sg`;
  };

  // E-Mail Text (ohne Markdown-Sterne, normale Formatierung)
  const createEmailText = () => {
    const weekText = isNextWeek ? ' (nächste Woche)' : '';
    const dateText = menu.formattedDate ? ` - ${menu.formattedDate}` : '';
    
    // Verwende Unicode-Escape-Sequenzen für E-Mail
    const dish = '\uD83C\uDF7D\uFE0F'; // 🍽️
    const calendar = '\uD83D\uDCC5'; // 📅
    const food = '\uD83C\uDF72'; // 🍲
    const money = '\uD83D\uDCB0'; // 💰
    const clock = '\u23F0'; // ⏰
    const location = '\uD83D\uDCCD'; // 📍
    const house = '\uD83C\uDFE0'; // 🏠
    const globe = '\uD83C\uDF10'; // 🌐
    
    return `${dish} Mittagsmenü${weekText}

${calendar} ${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}${dateText}

${food} ${menu.name}
${menu.description}

${money} CHF ${menu.price.toFixed(2)}
${clock} Servierzeit: 12:00 Uhr
${location} Bahnhofstrasse 19, Au St. Gallen

---
${house} Hopp Mittagsmenü Service
Täglich frische, regionale Küche
${globe} wochenmenu.hopp.sg`;
  };

  // WhatsApp teilen
  const shareViaWhatsApp = () => {
    const text = createWhatsAppText();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    setShowShareOptions(false);
  };

  // WhatsApp mit Bild-Link
  const shareWhatsAppWithImageLink = () => {
    const text = `${createWhatsAppText()}

📸 Bild ansehen: ${menu.image}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    setShowShareOptions(false);
  };

  // E-Mail Share
  const shareViaEmail = () => {
    const text = createEmailText();
    const weekText = isNextWeek ? ' (nächste Woche)' : '';
    const subject = `Mittagsmenü ${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}${weekText} - ${menu.name}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    window.location.href = emailUrl;
    setShowShareOptions(false);
  };

  // Text in Zwischenablage kopieren
  const copyToClipboard = async () => {
    try {
      const text = createWhatsAppText();
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setShowShareOptions(false);
      }, 2000);
    } catch (err) {
      console.error('Fehler beim Kopieren:', err);
    }
  };

  // 🔥 VERBESSERTE FUNKTION: Erstelle .eml-Datei mit eingebettetem Bild
  const downloadEmlWithEmbeddedImage = async () => {
    setIsGeneratingEml(true);
    
    try {
      console.log('🖼️ Starting EML generation with embedded image...');
      
      const weekText = isNextWeek ? ' (nächste Woche)' : '';
      const dateText = menu.formattedDate ? ` - ${menu.formattedDate}` : '';
      const subject = `Mittagsmenü ${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}${weekText} - ${menu.name}`;
      const currentDate = new Date().toUTCString();

      // 🔥 SCHRITT 1: Bild von URL laden und zu Base64 konvertieren
      let imageBase64 = '';
      let imageMimeType = 'image/jpeg';
      
      try {
        console.log('📥 Fetching image from URL:', menu.image);
        
        // Erstelle ein Proxy für CORS-Probleme
        const proxyUrl = `https://cors-anywhere.herokuapp.com/${menu.image}`;
        
        // Versuche zuerst die direkte URL, dann den Proxy
        let imageResponse;
        try {
          imageResponse = await fetch(menu.image);
          if (!imageResponse.ok) throw new Error('Direct fetch failed');
        } catch (directError) {
          console.log('Direct fetch failed, trying proxy...');
          imageResponse = await fetch(proxyUrl);
        }
        
        if (imageResponse.ok) {
          const imageBlob = await imageResponse.blob();
          imageMimeType = imageBlob.type || 'image/jpeg';
          
          // Konvertiere zu Base64
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = reader.result.split(',')[1]; // Entferne "data:image/...;base64,"
              resolve(base64);
            };
            reader.readAsDataURL(imageBlob);
          });
          
          console.log('✅ Image converted to base64, size:', imageBase64.length);
        }
      } catch (imageError) {
        console.warn('⚠️ Could not fetch image, creating email without embedded image:', imageError);
        // Fahre ohne eingebettetes Bild fort
      }

      // 🔥 SCHRITT 2: Erstelle HTML-Content für die E-Mail
      const htmlContent = `<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${subject}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); color: white; padding: 30px 20px; text-align: center; border-radius: 10px; margin-bottom: 30px; }
        .menu-image { width: 100%; max-width: 400px; height: 250px; object-fit: cover; border-radius: 10px; margin: 20px 0; display: block; margin-left: auto; margin-right: auto; }
        .menu-details { background: #f8f9fa; padding: 20px; border-radius: 10px; margin: 20px 0; }
        .price { font-size: 24px; font-weight: bold; color: #f97316; text-align: center; margin: 20px 0; }
        .info-section { background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 15px 0; }
        .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #f97316; color: #666; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🍽️ Mittagsmenü${weekText}</h1>
        <p>📅 ${menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}${dateText}</p>
    </div>
    
    ${imageBase64 ? `<img src="cid:menu-image" alt="${menu.name}" class="menu-image">` : `<div style="text-align: center; color: #666; margin: 20px 0;">📸 Bild: ${menu.image}</div>`}
    
    <div class="menu-details">
        <h2>🍲 ${menu.name}</h2>
        <p>${menu.description}</p>
    </div>
    
    <div class="price">💰 CHF ${menu.price.toFixed(2)}</div>
    
    <div class="info-section">
        <h3>📍 Alle Informationen:</h3>
        <ul>
            <li><strong>⏰ Servierzeit:</strong> 12:00 Uhr</li>
            <li><strong>📍 Adresse:</strong> Bahnhofstrasse 19, Au St. Gallen</li>
            <li><strong>🅿️ Parkplätze:</strong> Direkt beim Bahnhof verfügbar</li>
            <li><strong>🚊 Öffentlicher Verkehr:</strong> Optimal erreichbar</li>
        </ul>
    </div>
    
    <div class="info-section">
        <h3>🎁 Ihre Vorteile:</h3>
        <ul>
            <li><strong>Ab 4 Personen:</strong> Kostenlosen Kaffee für alle</li>
            <li><strong>Ab 6 Personen:</strong> Kostenloses Dessert für die ganze Gruppe</li>
            <li><strong>Wochenanmeldung:</strong> Zusätzliches kostenloses Dessert</li>
        </ul>
    </div>
    
    <div class="footer">
        <h3>🏠 Hopp Mittagsmenü Service</h3>
        <p>Täglich frische, regionale Küche</p>
        <p>🌐 <a href="https://wochenmenu.hopp.sg" style="color: #f97316;">wochenmenu.hopp.sg</a></p>
        <p>📧 <a href="mailto:news@hopp.sg" style="color: #f97316;">news@hopp.sg</a></p>
        <p>📞 <a href="tel:+41786566575" style="color: #f97316;">+41 78 656 65 75</a></p>
    </div>
</body>
</html>`;

      // 🔥 SCHRITT 3: Erstelle .eml-Datei im MIME-Format
      const boundary = `----=_NextPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      let emlContent = `From: HOPP Mittagsmenü Service <news@hopp.sg>
To: 
Subject: ${subject}
Date: ${currentDate}
Message-ID: <${Date.now()}.hopp@newsletter>
MIME-Version: 1.0
Content-Type: multipart/related; boundary="${boundary}"

--${boundary}
Content-Type: text/html; charset=utf-8
Content-Transfer-Encoding: 8bit

${htmlContent}

`;

      // 🔥 SCHRITT 4: Füge eingebettetes Bild hinzu (falls verfügbar)
      if (imageBase64) {
        emlContent += `--${boundary}
Content-Type: ${imageMimeType}
Content-Transfer-Encoding: base64
Content-ID: <menu-image>
Content-Disposition: inline; filename="menu-image.jpg"

${imageBase64}

`;
      }

      emlContent += `--${boundary}--`;

      // 🔥 SCHRITT 5: Erstelle Download
      const blob = new Blob([emlContent], { type: 'message/rfc822' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      const filename = `menu-${menu.day}-${isNextWeek ? 'naechste-woche' : 'diese-woche'}.eml`;
      link.href = url;
      link.download = filename;
      link.setAttribute('type', 'message/rfc822');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✅ EML file generated successfully with embedded image');
      
      // Zeige Erfolgs-Hinweis
      setTimeout(() => {
        alert(`📧 E-Mail-Datei "${filename}" wurde heruntergeladen!\n\n✅ Das Menübild ist direkt in die E-Mail eingebettet.\n\n📱 So verwenden Sie die Datei:\n• Doppelklick öffnet sie in Mac Mail/Outlook\n• Empfänger hinzufügen und versenden\n• Alle Bilder werden automatisch angezeigt`);
      }, 500);

    } catch (error) {
      console.error('❌ Error generating EML with embedded image:', error);
      alert('Fehler beim Erstellen der E-Mail-Datei. Bitte versuchen Sie es erneut.');
    } finally {
      setIsGeneratingEml(false);
      setShowShareOptions(false);
    }
  };

  return (
    <>
      {/* Share Button */}
      <div className="relative">
        <motion.button
          onClick={() => setShowShareOptions(!showShareOptions)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center"
          title="Menü teilen"
        >
          <SafeIcon icon={FiShare2} />
        </motion.button>
      </div>

      {/* Share Options Modal */}
      {showShareOptions && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50" 
            onClick={() => setShowShareOptions(false)} 
          />

          {/* Share Options Modal - Centered */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 w-[90vw] max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Menü teilen</h3>
              <button
                onClick={() => setShowShareOptions(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Standard WhatsApp */}
              <motion.button
                onClick={shareViaWhatsApp}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center space-x-3 p-3 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 transition-colors"
              >
                <SafeIcon icon={FiMessageCircle} className="text-xl" />
                <div className="text-left">
                  <div className="font-medium">WhatsApp</div>
                  <div className="text-sm opacity-75">Menü-Text teilen</div>
                </div>
              </motion.button>

              {/* WhatsApp mit Bild-Link */}
              <motion.button
                onClick={shareWhatsAppWithImageLink}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center space-x-3 p-3 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                  <SafeIcon icon={FiMessageCircle} className="text-white text-sm" />
                </div>
                <div className="text-left">
                  <div className="font-medium">WhatsApp + Bild-Link</div>
                  <div className="text-sm opacity-75">Text mit Bild-URL</div>
                </div>
              </motion.button>

              {/* E-Mail mit eingebettetem Bild */}
              <motion.button
                onClick={downloadEmlWithEmbeddedImage}
                disabled={isGeneratingEml}
                whileHover={{ scale: isGeneratingEml ? 1 : 1.02 }}
                whileTap={{ scale: isGeneratingEml ? 1 : 0.98 }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  isGeneratingEml 
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                    : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                }`}
              >
                <SafeIcon icon={isGeneratingEml ? FiLoader : FiDownload} className={`text-xl ${isGeneratingEml ? 'animate-spin' : ''}`} />
                <div className="text-left">
                  <div className="font-medium">
                    {isGeneratingEml ? 'Generiere E-Mail...' : 'E-Mail mit Bild (.eml)'}
                  </div>
                  <div className="text-sm opacity-75">
                    {isGeneratingEml ? 'Bild wird eingebettet...' : 'Für Mac Mail/Outlook mit Bild'}
                  </div>
                </div>
              </motion.button>

              {/* Standard E-Mail */}
              <motion.button
                onClick={shareViaEmail}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center space-x-3 p-3 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors"
              >
                <SafeIcon icon={FiMail} className="text-xl" />
                <div className="text-left">
                  <div className="font-medium">Standard E-Mail</div>
                  <div className="text-sm opacity-75">Nur Text (ohne Bild)</div>
                </div>
              </motion.button>

              {/* Text kopieren */}
              <motion.button
                onClick={copyToClipboard}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  copySuccess 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                }`}
              >
                <SafeIcon icon={copySuccess ? FiCheck : FiCopy} className="text-xl" />
                <div className="text-left">
                  <div className="font-medium">
                    {copySuccess ? 'Kopiert!' : 'Text kopieren'}
                  </div>
                  <div className="text-sm opacity-75">
                    {copySuccess ? 'In Zwischenablage' : 'Für andere Apps'}
                  </div>
                </div>
              </motion.button>
            </div>

            {/* Vorschau */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h4 className="text-xs font-medium text-gray-600 mb-3">Vorschau:</h4>
              
              {/* Menübild */}
              <div className="mb-3">
                <img 
                  src={menu.image} 
                  alt={menu.name} 
                  className="w-full h-32 object-cover rounded-lg border border-gray-200" 
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>

              {/* Text-Vorschau */}
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600 max-h-32 overflow-y-auto">
                <div className="whitespace-pre-wrap font-mono text-xs leading-relaxed">
                  {createWhatsAppText()}
                </div>
              </div>

              {/* Hinweise */}
              <div className="mt-3 space-y-2">
                <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                  📧 <strong>E-Mail mit Bild:</strong> Das Menübild wird direkt in die E-Mail eingebettet - perfekt für Mac Mail und Outlook!
                </div>
                
                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                  💡 <strong>WhatsApp + Bild-Link:</strong> Fügt den direkten Bild-Link zum Text hinzu
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
};

export default MenuShareButton;